import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Quotes, ArrowClockwise, ShareFat, Check } from '@phosphor-icons/react';
import { toPng } from 'html-to-image';
import { ShareCard } from './ShareCard';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname))
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;

const TAG_STYLES = {
  focus:     { ring: 'ring-[#f59e0b]/30', dot: 'bg-[#f59e0b]', label: 'Focus' },
  money:     { ring: 'ring-[#0f6b3a]/30', dot: 'bg-[#0f6b3a]', label: 'Money' },
  calm:      { ring: 'ring-[#0ea5e9]/30', dot: 'bg-[#0ea5e9]', label: 'Calm' },
  momentum:  { ring: 'ring-[#7c3aed]/30', dot: 'bg-[#7c3aed]', label: 'Momentum' },
  curiosity: { ring: 'ring-[#db2777]/30', dot: 'bg-[#db2777]', label: 'Curiosity' },
};

/**
 * DailyQuote — a single, personalised line of encouragement above the hero
 * briefing. Silent on failure (never blocks the dashboard from rendering).
 */
export const DailyQuote = () => {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const shareRef = useRef(null);

  const load = async (refresh = false) => {
    try {
      if (refresh) setRefreshing(true);
      const res = await axios.get(
        `${API_URL}/api/dashboard/daily-quote${refresh ? '?refresh=true' : ''}`,
        { withCredentials: true, timeout: 12000 }
      );
      setData(res.data);
    } catch {
      /* silent */
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const handleShare = async () => {
    if (!shareRef.current || sharing) return;
    setSharing(true);
    try {
      // Render to PNG. pixelRatio=1 because the node is already at full 1080x1920.
      const dataUrl = await toPng(shareRef.current, {
        cacheBust: true,
        pixelRatio: 1,
        width: 1080,
        height: 1920,
      });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `lifeadmin-quote-${data?.date || 'today'}.png`, { type: 'image/png' });

      // Prefer native share sheet on mobile (IG/WhatsApp/etc.); fall back to download.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'My LifeAdmin quote',
          text: `"${data.quote}" — my daily quote from LifeAdmin AI`,
        });
      } else {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setShared(true);
      setTimeout(() => setShared(false), 2400);
    } catch (err) {
      // User cancelled the native share sheet OR capture failed — silent.
      if (err?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.warn('Share failed:', err);
      }
    } finally {
      setSharing(false);
    }
  };

  if (!data?.quote) return null;

  const style = TAG_STYLES[data.tag] || TAG_STYLES.focus;

  return (
    <div
      className={`relative rounded-2xl bg-white border border-[#ebeef2] ring-1 ${style.ring} px-5 py-4 mb-4 shadow-sm overflow-hidden`}
      data-testid="daily-quote"
    >
      <Quotes
        size={64}
        weight="fill"
        className="absolute -top-3 -left-2 text-[#f2f4f6] pointer-events-none"
        aria-hidden
      />
      <div className="relative flex items-start gap-3">
        <div className="flex-1 min-w-0 pl-8">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
            <span className="text-[9px] font-bold uppercase tracking-widest text-[#596065]">
              {style.label} · for {data.for_name}
            </span>
          </div>
          <p
            className="text-[#1e1e2e] text-base lg:text-lg leading-snug font-medium italic"
            data-testid="daily-quote-text"
          >
            {data.quote}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={handleShare}
            disabled={sharing}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors disabled:opacity-50 ${
              shared
                ? 'bg-[#ecfdf5] text-[#0f6b3a] border-[#bbf7d0]'
                : 'bg-white text-[#005bc1] border-[#d6e3ff] hover:bg-[#eef4ff]'
            }`}
            title="Share as image"
            data-testid="daily-quote-share"
          >
            {shared ? <Check size={12} weight="bold" /> : <ShareFat size={12} weight="fill" />}
            {shared ? 'Saved' : sharing ? 'Creating…' : 'Share'}
          </button>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing}
            className="p-1.5 rounded-lg text-[#acb3b8] hover:text-[#596065] hover:bg-[#f2f4f6] transition-colors disabled:opacity-50"
            title="New quote"
            aria-label="Refresh quote"
            data-testid="daily-quote-refresh"
          >
            <ArrowClockwise size={14} weight="bold" className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      {/* Off-screen shareable card — measured + rasterized by html-to-image */}
      <ShareCard
        ref={shareRef}
        quote={data.quote}
        forName={data.for_name}
        tag={data.tag}
        date={data.date}
      />
    </div>
  );
};
